// Thin pub/sub wrapper around the lobby/game protocol (see protocol.ts).
// main.ts is the only consumer — this module owns the PeerJS session
// lifecycle so main.ts doesn't have to know whether it's talking to a host
// in-process or over a DataConnection.
//
// Two roles, chosen by which message first establishes a session:
//   - host: this tab creates a room (its PeerJS peer id = the room code)
//     and runs the authoritative Room via HostSession (hostRoom.ts). Other
//     players connect to it; it never dials out itself.
//   - client: this tab joins an existing room by dialing the host's peer
//     id directly, over two DataConnections — 'ctrl' (reliable, everything
//     except position) and 'path' (unreliable, player_path_update only).

import type { DataConnection, Peer } from 'peerjs';
import { dial, isUnavailableId, openClientPeer, openHostPeer, waitForOpen, watchConnectionHealth } from './peer';
import { HostSession } from './hostRoom';
import { generateRoomCode } from './rooms';
import type { ClientMessage, ServerMessage } from './protocol';

type Listener = (msg: ServerMessage) => void;

type Session =
  | { role: 'host'; host: HostSession }
  | { role: 'client'; peer: Peer; reliable: DataConnection; unreliable: DataConnection | null };

let session: Session | null = null;
let currentPlayerId: string | null = null;
const listeners = new Map<ServerMessage['type'], Set<Listener>>();

function deliverServerMessage(msg: ServerMessage): void {
  if (msg.type === 'room_joined') currentPlayerId = msg.playerId;
  const set = listeners.get(msg.type);
  if (set) set.forEach((fn) => fn(msg));
}

async function createAsHost(name: string): Promise<void> {
  for (let attempt = 0; attempt < 5; attempt++) {
    const code = generateRoomCode();
    try {
      const peer = await openHostPeer(code);
      session = { role: 'host', host: new HostSession(peer, code, name, deliverServerMessage) };
      return;
    } catch (err) {
      if (!isUnavailableId(err)) break;
    }
  }
  deliverServerMessage({ type: 'error', message: 'Could not create a room. Please try again.' });
}

function teardownClient(): void {
  if (session?.role === 'client') {
    session.reliable.close();
    session.unreliable?.close();
    session.peer.destroy();
  }
  session = null;
  currentPlayerId = null;
}

function handleHostLost(): void {
  if (!session || session.role !== 'client') return; // already torn down
  deliverServerMessage({ type: 'error', message: 'Connection to the host was lost. The game has ended.' });
  teardownClient();
}

async function joinAsClient(code: string, name: string): Promise<void> {
  try {
    const peer = await openClientPeer();
    const reliable = dial(peer, code, 'ctrl', true);
    const unreliable = dial(peer, code, 'path', false);
    await waitForOpen(reliable, 8000);
    reliable.on('data', (raw) => deliverServerMessage(raw as ServerMessage));
    watchConnectionHealth(reliable, handleHostLost);
    unreliable.on('data', (raw) => deliverServerMessage(raw as ServerMessage));
    session = { role: 'client', peer, reliable, unreliable };
    reliable.send({ type: 'join_room', code, name } satisfies ClientMessage);
  } catch {
    deliverServerMessage({ type: 'error', message: 'Could not reach that room. Check the code and try again.' });
  }
}

export function connect(): Promise<void> {
  return Promise.resolve();
}

export function send(msg: ClientMessage): void {
  if (msg.type === 'create_room') { void createAsHost(msg.name); return; }
  if (msg.type === 'join_room') { void joinAsClient(msg.code, msg.name); return; }
  if (!session) return;
  if (session.role === 'host') {
    if (msg.type === 'leave_room') { session.host.destroy(); session = null; currentPlayerId = null; return; }
    session.host.handleLocal(msg);
    return;
  }
  if (msg.type === 'leave_room') { teardownClient(); return; }
  const conn = msg.type === 'player_path_update' ? session.unreliable : session.reliable;
  if (conn?.open) conn.send(msg);
}

export function on<K extends ServerMessage['type']>(
  type: K,
  handler: (msg: Extract<ServerMessage, { type: K }>) => void,
): () => void {
  let set = listeners.get(type);
  if (!set) {
    set = new Set();
    listeners.set(type, set);
  }
  set.add(handler as Listener);
  return () => set!.delete(handler as Listener);
}

export function myId(): string | null {
  return currentPlayerId;
}

export function isConnected(): boolean {
  if (!session) return false;
  return session.role === 'host' ? true : session.reliable.open;
}
